/**
 * Normalized realtime entity store with per-key subscriptions (decision D6,
 * docs/architecture/06-frontend.md §2).
 *
 *  - entities live in a Map that is never replaced; a patch replaces only the patched entry;
 *  - patches are coalesced and applied once per animation frame (batching);
 *  - listeners of a key are notified only when that key changed (O(subscribers of the key));
 *  - `sv` (state_version) makes stale / duplicate / out-of-order patches no-ops.
 */
export type Listener = () => void;
export type ChangeListener = (changed: ReadonlySet<string>) => void;
export type Versioned = { sv: number };
export type Scheduler = (flush: () => void) => void;
/** Combines a base (current entity or pending delta) with a new delta; default = shallow merge. */
export type Merge<T> = (base: Partial<T> | undefined, delta: Partial<T>) => Partial<T>;
const shallow = <T>(base: Partial<T> | undefined, delta: Partial<T>): Partial<T> => ({ ...base, ...delta });

const rafScheduler: Scheduler = (flush) =>
  typeof requestAnimationFrame === 'function' ? requestAnimationFrame(() => flush()) : setTimeout(flush, 16);

export class EntityStore<T extends Versioned> {
  private readonly byId = new Map<string, T>();
  private readonly keyListeners = new Map<string, Set<Listener>>();
  private readonly changeListeners = new Set<ChangeListener>();
  private readonly pending = new Map<string, Partial<T> & Versioned>();
  private scheduled = false;

  constructor(
    private readonly schedule: Scheduler = rafScheduler,
    private readonly merge: Merge<T> = shallow,
  ) {}

  get(id: string): T | undefined {
    return this.byId.get(id);
  }

  get size(): number {
    return this.byId.size;
  }

  ids(): IterableIterator<string> {
    return this.byId.keys();
  }

  /** Queue a delta; applied on the next flush. Deltas for the same id merge (latest field wins). */
  patch(id: string, delta: Partial<T> & Versioned): void {
    const prev = this.pending.get(id);
    this.pending.set(id, prev ? { ...this.merge(prev, delta), sv: Math.max(prev.sv, delta.sv) } : delta);
    if (!this.scheduled) {
      this.scheduled = true;
      this.schedule(this.flush);
    }
  }

  /** Drops every entity (sign-out: the next user must not see or be blocked by previous state). */
  clear(): void {
    const ids = new Set(this.byId.keys());
    this.byId.clear();
    this.pending.clear();
    for (const id of ids) this.keyListeners.get(id)?.forEach((l) => l());
    if (ids.size) this.changeListeners.forEach((l) => l(ids));
  }

  /** Drops one entity (it left the registry); its listeners and the change listeners are told at once. */
  remove(id: string): void {
    this.pending.delete(id);
    if (!this.byId.delete(id)) return;
    this.keyListeners.get(id)?.forEach((l) => l());
    const changed = new Set([id]);
    this.changeListeners.forEach((l) => l(changed));
  }

  /**
   * Snapshot reconciliation after (re)connect: only entries with a newer sv are touched,
   * unchanged entities keep their object identity (no rerender).
   */
  reconcile(items: Iterable<[string, T]>): void {
    for (const [id, item] of items) this.patch(id, item);
  }

  /** Applies pending patches now (called by the scheduler; tests may call it directly). */
  flush = (): void => {
    this.scheduled = false;
    if (this.pending.size === 0) return;
    const changed = new Set<string>();
    for (const [id, delta] of this.pending) {
      const cur = this.byId.get(id);
      if (cur && delta.sv <= cur.sv) continue;
      this.byId.set(id, (cur ? { ...this.merge(cur, delta), sv: delta.sv } : delta) as T);
      changed.add(id);
    }
    this.pending.clear();
    for (const id of changed) this.keyListeners.get(id)?.forEach((l) => l());
    if (changed.size) this.changeListeners.forEach((l) => l(changed));
  };

  subscribeKey(id: string, listener: Listener): () => void {
    let set = this.keyListeners.get(id);
    if (!set) this.keyListeners.set(id, (set = new Set()));
    set.add(listener);
    return () => {
      set!.delete(listener);
      if (set!.size === 0) this.keyListeners.delete(id);
    };
  }

  /** For consumers that need the set of changed ids (map layer, aggregate counters). */
  subscribeChanges(listener: ChangeListener): () => void {
    this.changeListeners.add(listener);
    return () => this.changeListeners.delete(listener);
  }

  listenerCount(id: string): number {
    return this.keyListeners.get(id)?.size ?? 0;
  }
}
